/**
 * CalculatorOverlay – einfacher Taschenrechner für das Whiteboard/Display.
 * Dark-Theme, Pill-Buttons, Orange für Operationen. Mit x², √, x^n.
 */
import { useRef, useState, useCallback } from 'react';

export interface CalculatorState {
  visible: boolean;
  x: number;
  y: number;
}

export const DEFAULT_CALCULATOR: CalculatorState = {
  visible: false,
  x: 760,
  y: 200,
};

type Op = '+' | '-' | '×' | '÷' | '^' | null;

function parseDisplay(s: string): number {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/** Anzahl der angezeigten Dezimalstellen (Darstellung). */
const DISPLAY_DECIMAL_PLACES = 8;
/** Höhere interne Genauigkeit für Berechnungen (größer als Anzeige). */
const INTERNAL_DECIMAL_PLACES = 15;

function formatDisplay(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return String(n);
  let s = n.toFixed(DISPLAY_DECIMAL_PLACES).replace(/0+$/, '');
  if (s.endsWith('.')) s = s.slice(0, -1);
  s = s.replace('.', ',');
  return s.length > 18 ? n.toExponential(6).replace('.', ',') : s;
}

interface CalculatorOverlayProps {
  x: number;
  y: number;
  displayScale?: number;
  interactive?: boolean;
  onMove?: (x: number, y: number) => void;
}

export function CalculatorOverlay({
  x,
  y,
  displayScale = 1,
  interactive = true,
  onMove,
}: CalculatorOverlayProps) {
  const [display, setDisplay] = useState('0');
  const [pendingOp, setPendingOp] = useState<Op>(null);
  const [pendingValue, setPendingValue] = useState<number | null>(null);
  const [clearOnNextDigit, setClearOnNextDigit] = useState(false);
  const [lastOp, setLastOp] = useState<Op>(null);
  const [lastSecondOperand, setLastSecondOperand] = useState<number | null>(null);
  const dragStartRef = useRef<{ clientX: number; clientY: number; x: number; y: number } | null>(null);
  /** Letztes Berechnungsergebnis mit interner Genauigkeit (für Folgerechnungen). */
  const lastResultRef = useRef<number | null>(null);

  const applyUnary = useCallback((fn: (n: number) => number) => {
    const n = (clearOnNextDigit && lastResultRef.current !== null) ? lastResultRef.current : parseDisplay(display);
    const result = fn(n);
    lastResultRef.current = Number(Number(result).toFixed(INTERNAL_DECIMAL_PLACES));
    setDisplay(formatDisplay(result));
    setClearOnNextDigit(true);
  }, [display, clearOnNextDigit]);

  const compute = useCallback((a: number, op: Op, b: number): number => {
    if (op === null) return b;
    switch (op) {
      case '+': return a + b;
      case '-': return a - b;
      case '×': return a * b;
      case '÷': return a / b;
      case '^': return Math.pow(a, b);
    }
  }, []);

  const applyBinary = useCallback((nextOp: Op) => {
    const current = (clearOnNextDigit && lastResultRef.current !== null) ? lastResultRef.current : parseDisplay(display);
    let result = current;
    if (pendingOp !== null && pendingValue !== null) {
      result = compute(pendingValue, pendingOp, current);
      setLastOp(pendingOp);
      setLastSecondOperand(current);
    } else if (nextOp === null && lastOp !== null && lastSecondOperand !== null) {
      result = compute(current, lastOp, lastSecondOperand);
    }
    const precise = Number(Number(result).toFixed(INTERNAL_DECIMAL_PLACES));
    lastResultRef.current = precise;
    setDisplay(formatDisplay(result));
    setPendingValue(nextOp !== null ? precise : null);
    setPendingOp(nextOp);
    setClearOnNextDigit(true);
  }, [display, clearOnNextDigit, pendingOp, pendingValue, lastOp, lastSecondOperand, compute]);

  const handleDigit = useCallback((digit: string) => {
    lastResultRef.current = null;
    setDisplay((prev) => {
      if (clearOnNextDigit) return digit;
      if (prev === '0' && digit !== ',') return digit;
      if (digit === ',' && prev.includes(',')) return prev;
      return prev + digit;
    });
    setClearOnNextDigit(false);
  }, [clearOnNextDigit]);

  const handleBackspace = useCallback(() => {
    lastResultRef.current = null;
    setDisplay((prev) => {
      if (clearOnNextDigit || prev.length <= 1) return '0';
      return prev.slice(0, -1);
    });
    setClearOnNextDigit(false);
  }, [clearOnNextDigit]);

  const handleAc = useCallback(() => {
    lastResultRef.current = null;
    setDisplay('0');
    setPendingOp(null);
    setPendingValue(null);
    setLastOp(null);
    setLastSecondOperand(null);
    setClearOnNextDigit(false);
  }, []);

  const handleOp = useCallback((op: Op) => {
    applyBinary(op);
  }, [applyBinary]);

  const handlePower = useCallback(() => {
    const base = (clearOnNextDigit && lastResultRef.current !== null) ? lastResultRef.current : parseDisplay(display);
    setPendingValue(base);
    setPendingOp('^');
    setClearOnNextDigit(true);
  }, [display, clearOnNextDigit]);

  const handleSign = useCallback(() => {
    applyUnary((n) => -n);
  }, [applyUnary]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!interactive || !onMove) return;
    e.preventDefault();
    dragStartRef.current = { clientX: e.clientX, clientY: e.clientY, x, y };
    try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ok */ }
  }, [interactive, onMove, x, y]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const start = dragStartRef.current;
    if (!start || !onMove) return;
    const dx = (e.clientX - start.clientX) / displayScale;
    const dy = (e.clientY - start.clientY) / displayScale;
    onMove(start.x + dx, start.y + dy);
  }, [onMove, displayScale]);

  const handlePointerUp = useCallback(() => {
    dragStartRef.current = null;
  }, []);

  const btnNum = 'h-11 w-11 min-h-[2.75rem] min-w-[2.75rem] rounded-full bg-slate-700 text-white text-lg font-medium hover:bg-slate-600 active:bg-slate-500 border-0 flex-shrink-0';
  const btnOp = 'h-11 w-11 min-h-[2.75rem] min-w-[2.75rem] rounded-full bg-orange-500 text-white text-lg font-medium hover:bg-orange-400 active:bg-orange-600 border-0 flex-shrink-0';
  const btnFn = 'h-11 w-11 min-h-[2.75rem] min-w-[2.75rem] rounded-full bg-slate-600 text-white text-sm font-medium hover:bg-slate-500 active:bg-slate-400 border-0 flex-shrink-0';

  return (
    <div
      className="absolute z-[18] touch-none select-none rounded-2xl border border-slate-600 bg-slate-900 shadow-2xl overflow-visible min-w-[220px] w-max flex-shrink-0"
      style={{
        left: 0,
        top: 0,
        transform: `translate(${x}px, ${y}px)`,
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {/* Titel zum Verschieben */}
      <div
        className="flex items-center justify-end gap-1 px-3 py-1.5 bg-slate-800 cursor-move border-b border-slate-600"
        onPointerDown={handlePointerDown}
      >
        <span className="text-[10px] text-slate-400 uppercase tracking-wider">Taschenrechner</span>
      </div>
      <div className="p-3 flex-shrink-0">
        <div className="mb-3 flex justify-end pr-1 min-h-[2.5rem]">
          <span className="text-3xl font-light tabular-nums text-white truncate max-w-[240px] block text-right" aria-live="polite">
            {display}
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2 w-max min-w-[200px] flex-shrink-0">
          {/* Zeile 0: x², √, x^n, ÷ */}
          <button type="button" className={btnFn} onClick={() => applyUnary((n) => n * n)} title="Quadrat">x²</button>
          <button type="button" className={btnFn} onClick={() => applyUnary((n) => Math.sqrt(n))} title="Wurzel">√</button>
          <button type="button" className={btnFn} onClick={handlePower} title="Hoch">x^n</button>
          <button type="button" className={btnOp} onClick={() => handleOp('÷')}>÷</button>
          {/* Zeile 1: ⌫, AC, %, × */}
          <button type="button" className={btnFn} onClick={handleBackspace} title="Rücktaste">⌫</button>
          <button type="button" className={btnFn} onClick={handleAc}>AC</button>
          <button type="button" className={btnFn} onClick={() => applyUnary((n) => n / 100)}>%</button>
          <button type="button" className={btnOp} onClick={() => handleOp('×')}>×</button>
          {/* 7 8 9 − */}
          <button type="button" className={btnNum} onClick={() => handleDigit('7')}>7</button>
          <button type="button" className={btnNum} onClick={() => handleDigit('8')}>8</button>
          <button type="button" className={btnNum} onClick={() => handleDigit('9')}>9</button>
          <button type="button" className={btnOp} onClick={() => handleOp('-')}>−</button>
          {/* 4 5 6 + */}
          <button type="button" className={btnNum} onClick={() => handleDigit('4')}>4</button>
          <button type="button" className={btnNum} onClick={() => handleDigit('5')}>5</button>
          <button type="button" className={btnNum} onClick={() => handleDigit('6')}>6</button>
          <button type="button" className={btnOp} onClick={() => handleOp('+')}>+</button>
          {/* 1 2 3 (Platzhalter für Grid) */}
          <button type="button" className={btnNum} onClick={() => handleDigit('1')}>1</button>
          <button type="button" className={btnNum} onClick={() => handleDigit('2')}>2</button>
          <button type="button" className={btnNum} onClick={() => handleDigit('3')}>3</button>
          <div className="h-11 min-h-[2.75rem] min-w-[2.75rem] w-11 flex-shrink-0" aria-hidden />
          {/* +/-, 0, ,, = */}
          <button type="button" className={btnFn} onClick={handleSign}>+/−</button>
          <button type="button" className={btnNum} onClick={() => handleDigit('0')}>0</button>
          <button type="button" className={btnNum} onClick={() => handleDigit(',')}>,</button>
          <button type="button" className={btnOp} onClick={() => handleOp(null)}>=</button>
        </div>
      </div>
    </div>
  );
}
